<template>
  <div class="flex flex-col mt-32 px-4 pb-16">
    <div class="mb-6 flex flex-wrap items-center justify-between gap-3">
      <div>
        <h2 class="text-2xl font-bold text-blueGray-700">Employee Enrollment Status</h2>
        <p class="text-sm text-blueGray-400 mt-1">
          Invite employees and monitor onboarding progress. Sensitive employee documents are never shown here.
        </p>
      </div>
      <div class="flex gap-2 flex-wrap">
        <button class="action-btn action-btn-secondary" :disabled="loading" @click="fetchStatus">
          <i class="fas fa-sync-alt mr-2" :class="{ 'fa-spin': loading }"></i>Refresh
        </button>
        <button class="action-btn action-btn-primary" @click="inviteEmployee">
          <i class="fas fa-envelope mr-2"></i>Invite Employee
        </button>
      </div>
    </div>

    <div v-if="errorMessage" class="alert-card alert-error mb-5">
      <i class="fas fa-exclamation-triangle mr-2"></i>{{ errorMessage }}
    </div>

    <div class="summary-grid mb-6">
      <div class="summary-card">
        <div class="summary-value">{{ employees.length }}</div>
        <div class="summary-label">Connected Employees</div>
      </div>
      <div class="summary-card">
        <div class="summary-value">{{ pendingInvitations.length }}</div>
        <div class="summary-label">Pending Invitations</div>
      </div>
      <div class="summary-card">
        <div class="summary-value">{{ submittedCount }}</div>
        <div class="summary-label">Enrollment Submitted</div>
      </div>
      <div class="summary-card">
        <div class="summary-value">{{ inProgressCount }}</div>
        <div class="summary-label">In Progress</div>
      </div>
    </div>

    <div v-if="pendingInvitations.length" class="panel mb-6">
      <div class="panel-header">
        <div>
          <h3>Pending Invitations</h3>
          <p>These employees have not completed the invitation yet.</p>
        </div>
      </div>
      <div class="divide-y divide-gray-100">
        <div v-for="invite in pendingInvitations" :key="invite.id" class="invite-row">
          <div>
            <div class="font-semibold text-blueGray-700">{{ invite.email }}</div>
            <div class="text-xs text-blueGray-400">Invited {{ formatDate(invite.created_at) }}</div>
          </div>
          <span class="status-pill status-pending">Invitation Pending</span>
        </div>
      </div>
    </div>

    <div class="panel">
      <div class="panel-header">
        <div>
          <h3>Employee Enrollment</h3>
          <p>Only completion metadata is visible to the client.</p>
        </div>
      </div>

      <div v-if="loading" class="empty-state">
        <i class="fas fa-spinner fa-spin text-3xl mb-3"></i>
        <p>Loading employee enrollment status...</p>
      </div>

      <div v-else-if="employees.length === 0" class="empty-state">
        <i class="fas fa-user-plus text-3xl mb-3"></i>
        <p class="font-semibold">No connected employees yet.</p>
        <p class="text-sm mt-1">Invite an employee to begin the onboarding process.</p>
      </div>

      <div v-else class="employee-list">
        <div v-for="employee in employees" :key="employee.hire_id || employee.employee_id" class="employee-card">
          <div class="employee-card-header">
            <div>
              <div class="flex flex-wrap items-center gap-2">
                <h4>{{ employee.name || 'Employee' }}</h4>
                <span :class="relationshipClass(employee.relationship_status)" class="status-pill">
                  {{ employee.relationship_status || 'Unknown' }}
                </span>
              </div>
              <p class="text-sm text-blueGray-400 mt-1">{{ employee.email }}</p>
            </div>
            <div class="text-right">
              <span :class="overallStatusClass(employee)" class="status-pill status-pill-large">
                {{ overallStatus(employee) }}
              </span>
              <div v-if="employee.enrollment_updated_at" class="text-xs text-blueGray-400 mt-2">
                Updated {{ formatDate(employee.enrollment_updated_at) }}
              </div>
            </div>
          </div>

          <div v-if="!employee.enrollment_started" class="alert-card alert-info mt-4">
            <i class="fas fa-clock mr-2"></i>The employee has not started this employer-specific enrollment yet.
          </div>

          <div class="documents-grid mt-5">
            <div
              v-for="doc in visibleDocuments(employee)"
              :key="doc.key"
              class="document-status-card"
            >
              <div class="document-icon"><i :class="doc.icon"></i></div>
              <div class="min-w-0 flex-1">
                <div class="document-name">{{ doc.label }}</div>
                <div class="document-meta">{{ doc.required ? 'Required' : 'Not required' }}</div>
              </div>
              <span :class="documentStatusClass(doc.status)" class="status-pill">
                {{ documentStatusLabel(doc.status) }}
              </span>
            </div>
          </div>

          <div class="completion-row mt-5">
            <div>
              <div class="completion-label">Required documents</div>
              <div class="completion-value">
                {{ employee.required_documents_submitted ? 'Submitted' : 'Incomplete' }}
              </div>
            </div>
            <div>
              <div class="completion-label">Recorded signatures</div>
              <div class="completion-value">{{ employee.signature_count || 0 }}</div>
            </div>
            <div>
              <div class="completion-label">Driving documents</div>
              <div class="completion-value">{{ employee.driving_required ? 'Required' : 'Not required' }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/dist/sweetalert2.min.css';

export default {
  name: 'EmployeeEnrollmentWizard',
  data() {
    return {
      loading: false,
      employees: [],
      pendingInvitations: [],
      errorMessage: '',
      documentDefinitions: [
        { key: 'id_copy', label: 'ID Copy', icon: 'fas fa-id-card', drivingOnly: false },
        { key: 'social_security_card', label: 'Social Security Card', icon: 'fas fa-credit-card', drivingOnly: false },
        { key: 'live_scan', label: 'Live Scan', icon: 'fas fa-fingerprint', drivingOnly: false },
        { key: 'govt_approval', label: 'Government Approval', icon: 'fas fa-file-contract', drivingOnly: false },
        { key: 'identification_number_doc', label: 'Identification Number', icon: 'fas fa-file-alt', drivingOnly: false },
        { key: 'auto_insurance', label: 'Auto Insurance', icon: 'fas fa-car', drivingOnly: true },
        { key: 'driver_license', label: "Driver's License", icon: 'fas fa-id-badge', drivingOnly: true },
      ],
    };
  },
  computed: {
    submittedCount() {
      return this.employees.filter((employee) => this.overallStatus(employee) === 'Submitted').length;
    },
    inProgressCount() {
      return this.employees.filter((employee) => this.overallStatus(employee) === 'In Progress').length;
    },
  },
  methods: {
    authHeaders() {
      return { Authorization: `Bearer ${localStorage.getItem('token') || ''}` };
    },
    async fetchStatus() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/client/enrollment-status`,
          { headers: this.authHeaders() }
        );

        this.employees = Array.isArray(response.data?.employees) ? response.data.employees : [];
        this.pendingInvitations = Array.isArray(response.data?.pending_invitations)
          ? response.data.pending_invitations
          : [];
      } catch (error) {
        console.error('Enrollment status error:', error.response?.data || error);
        this.errorMessage =
          error.response?.data?.error ||
          error.response?.data?.message ||
          'Could not load employee enrollment status.';
      } finally {
        this.loading = false;
      }
    },
    async inviteEmployee() {
      const { value: email } = await Swal.fire({
        title: 'Invite Employee',
        text: 'Enter the employee email address. The employee will complete their own enrollment and document uploads.',
        input: 'email',
        inputPlaceholder: 'employee@example.com',
        showCancelButton: true,
        confirmButtonText: 'Send Invitation',
        confirmButtonColor: '#10b981',
        inputValidator: (value) => (!value ? 'Please enter an email address.' : undefined),
      });

      if (!email) return;

      Swal.fire({
        title: 'Sending invitation...',
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });

      try {
        const response = await axios.post(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/invite_employee`,
          { email },
          { headers: this.authHeaders() }
        );

        if (response.data?.email_sent === false && response.data?.signup_url) {
          await Swal.fire({
            icon: 'warning',
            title: 'Invitation created',
            text: `${response.data.message || 'Email delivery failed.'}\n\nManual signup link:\n${response.data.signup_url}`,
            confirmButtonText: 'OK',
          });
        } else {
          await Swal.fire({
            icon: 'success',
            title: 'Invitation sent',
            text: response.data?.message || `Invitation sent to ${email}`,
            timer: 2500,
            showConfirmButton: false,
          });
        }

        await this.fetchStatus();
      } catch (error) {
        console.error('Invite employee error:', error.response?.data || error);
        Swal.fire({
          icon: 'error',
          title: 'Invitation failed',
          text:
            error.response?.data?.error ||
            error.response?.data?.message ||
            'Could not send the invitation.',
        });
      }
    },
    visibleDocuments(employee) {
      return this.documentDefinitions
        .filter((doc) => !doc.drivingOnly || employee.driving_required)
        .map((doc) => ({
          ...doc,
          required: !doc.drivingOnly || Boolean(employee.driving_required),
          status: employee.documents?.[doc.key]?.status || 'not_submitted',
        }));
    },
    overallStatus(employee) {
      if (!employee.enrollment_started) return 'Not Started';
      if (employee.required_documents_submitted && Number(employee.signature_count || 0) >= 2) {
        return 'Submitted';
      }

      const docs = Object.values(employee.documents || {});
      const hasActivity = docs.some((doc) => doc?.submitted || doc?.status === 'rejected') || Number(employee.signature_count || 0) > 0;
      return hasActivity ? 'In Progress' : 'Started';
    },
    overallStatusClass(employee) {
      const status = this.overallStatus(employee);
      return {
        'status-approved': status === 'Submitted',
        'status-uploaded': status === 'In Progress',
        'status-pending': status === 'Started' || status === 'Not Started',
      };
    },
    relationshipClass(status) {
      return {
        'status-approved': status === 'Active',
        'status-pending': status === 'Pending for approval',
        'status-rejected': status === 'Declined' || status === 'Terminated',
      };
    },
    documentStatusClass(status) {
      return {
        'status-approved': status === 'approved',
        'status-uploaded': status === 'uploaded',
        'status-rejected': status === 'rejected',
        'status-pending': !status || status === 'not_submitted' || status === 'pending_upload',
      };
    },
    documentStatusLabel(status) {
      const labels = {
        approved: 'Approved',
        uploaded: 'Submitted',
        rejected: 'Rejected',
        pending_upload: 'Not Submitted',
        not_submitted: 'Not Submitted',
      };
      return labels[status] || 'Not Submitted';
    },
    formatDate(value) {
      if (!value) return 'N/A';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },
  },
  mounted() {
    this.fetchStatus();
  },
};
</script>

<style scoped>
.panel {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.85rem;
  overflow: hidden;
  box-shadow: 0 4px 18px rgba(15, 23, 42, 0.05);
}
.panel-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}
.panel-header h3 { margin: 0; font-size: 1rem; font-weight: 700; color: #334155; }
.panel-header p { margin: 0.2rem 0 0; font-size: 0.78rem; color: #94a3b8; }
.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
}
.summary-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1.1rem 1.25rem;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.04);
}
.summary-value { font-size: 1.7rem; font-weight: 800; color: #0f172a; }
.summary-label { margin-top: 0.25rem; font-size: 0.75rem; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; }
.invite-row { display: flex; justify-content: space-between; align-items: center; gap: 1rem; padding: 1rem 1.5rem; }
.employee-list { display: flex; flex-direction: column; gap: 1rem; padding: 1rem; }
.employee-card { border: 1px solid #e2e8f0; border-radius: 0.75rem; padding: 1.25rem; background: #fff; }
.employee-card-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; flex-wrap: wrap; }
.employee-card h4 { margin: 0; color: #1e293b; font-size: 1.05rem; font-weight: 750; }
.documents-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.75rem; }
.document-status-card { display: flex; align-items: center; gap: 0.75rem; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 0.65rem; padding: 0.8rem; }
.document-icon { width: 34px; height: 34px; border-radius: 8px; background: #e0f2fe; color: #0284c7; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.document-name { font-size: 0.82rem; font-weight: 700; color: #334155; }
.document-meta { font-size: 0.68rem; color: #94a3b8; margin-top: 0.1rem; }
.completion-row { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0.75rem; border-top: 1px solid #e2e8f0; padding-top: 1rem; }
.completion-label { font-size: 0.68rem; color: #94a3b8; text-transform: uppercase; font-weight: 700; letter-spacing: 0.04em; }
.completion-value { margin-top: 0.2rem; font-size: 0.85rem; color: #334155; font-weight: 700; }
.status-pill { display: inline-flex; align-items: center; border-radius: 9999px; padding: 0.25rem 0.65rem; font-size: 0.68rem; font-weight: 700; white-space: nowrap; }
.status-pill-large { padding: 0.35rem 0.8rem; font-size: 0.72rem; }
.status-approved { background: #dcfce7; color: #166534; }
.status-uploaded { background: #dbeafe; color: #1d4ed8; }
.status-pending { background: #fef3c7; color: #92400e; }
.status-rejected { background: #fee2e2; color: #991b1b; }
.action-btn { display: inline-flex; align-items: center; justify-content: center; border-radius: 0.5rem; padding: 0.65rem 1rem; font-size: 0.8rem; font-weight: 700; transition: 0.15s; }
.action-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.action-btn-primary { background: #10b981; color: #fff; }
.action-btn-primary:hover { background: #059669; }
.action-btn-secondary { background: #fff; color: #475569; border: 1px solid #cbd5e1; }
.action-btn-secondary:hover { background: #f8fafc; }
.alert-card { border-radius: 0.65rem; padding: 0.8rem 1rem; font-size: 0.8rem; }
.alert-error { background: #fef2f2; border: 1px solid #fecaca; color: #991b1b; }
.alert-info { background: #eff6ff; border: 1px solid #bfdbfe; color: #1e40af; }
.empty-state { padding: 4rem 1rem; text-align: center; color: #64748b; }
@media (max-width: 900px) {
  .summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .documents-grid { grid-template-columns: 1fr; }
}
@media (max-width: 560px) {
  .summary-grid, .completion-row { grid-template-columns: 1fr; }
  .invite-row { align-items: flex-start; flex-direction: column; }
}
</style>
